import type { IDropdownContextProps } from 'components/DropdownContext';
import { DropdownContext } from 'components/DropdownContext';
import { ContractStatusEnum, ProjectMethodologyEnum, ProjectStatusEnum } from 'gql/graphql';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useHandleEditContract, useHandleUploadContract } from 'hooks/useHandlersContract';
import { useCreateBimOrder } from 'pages/Project/BimProject/hooks/useCreateBimOrder';
import { methodologyMap } from 'types/methodology';
import { Tooltip } from 'uikit';

import { useMemo } from 'react';

interface IKebabTreeContract extends Partial<IDropdownContextProps> {
    projectId: string | undefined;
    methodology: string | undefined;
    orderRules: Record<string, boolean>;
    contractRules: Record<string, boolean>;
    statusProject: ProjectStatusEnum | undefined;
    onCreateWorkOrder: () => void;
    statusContract?: ContractStatusEnum;
    isAvailableWorkOrder?: boolean;
}

enum TooltipNameKebabTreeContract {
    UploadNewFile = 'UploadNewFile',
}

export const KebabTreeContract = ({
    projectId,
    methodology,
    orderRules,
    contractRules,
    statusContract,
    statusProject,
    isAvailableWorkOrder,
    onCreateWorkOrder,
    ...propsDropdown
}: IKebabTreeContract) => {
    const handleUploadContract = useHandleUploadContract(projectId, methodology);
    const handleEditContract = useHandleEditContract(projectId, methodology);

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;
    const isContractStatusCompleted = statusContract === ContractStatusEnum.Completed;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const { createBimOrderHandler, orderCreateBtnTitle, CreateOrderConfirmationDialog, isOrderCreateDisabled } =
        useCreateBimOrder({
            projectId,
        });

    const itemsContract = useMemo(() => {
        if (!projectId) {
            return [
                {
                    label: 'Загрузить новый файл',
                    action: handleUploadContract,
                    id: 2,
                    disabled: isDisabledByProjectStatus,
                },
            ];
        }

        if (methodology === methodologyMap[ProjectMethodologyEnum.Bim]) {
            return [
                {
                    id: 1,
                    label: orderCreateBtnTitle,
                    action: createBimOrderHandler,
                    disabled:
                        !orderRules.canCreate ||
                        !isContractStatusWork ||
                        isDisabledByProjectStatus ||
                        isOrderCreateDisabled,
                },
                {
                    id: 2,
                    label: 'Редактировать',
                    action: handleEditContract,
                    disabled: !contractRules?.canEdit || isDisabledByProjectStatus || isContractStatusCompleted,
                },
            ];
        }

        return [
            {
                id: 1,
                label: 'Создать Наряд-Заказ',
                action: onCreateWorkOrder,
                disabled: !orderRules.canCreate || !isContractStatusWork || isDisabledByProjectStatus,
            },
            {
                id: 2,
                label: 'Загрузить новый файл',
                action: handleUploadContract,
                disabled:
                    !isContractStatusWork ||
                    !contractRules?.canCreate ||
                    isDisabledByProjectStatus ||
                    isAvailableWorkOrder,
                haveTooltip: isAvailableWorkOrder,
                tooltipName: TooltipNameKebabTreeContract.UploadNewFile,
                tooltipMsg: 'По договору имеются Наряд-Заказы, загрузка нового файла недоступна',
            },
            {
                id: 3,
                label: 'Редактировать',
                action: handleEditContract,
                disabled: !contractRules?.canEdit || isDisabledByProjectStatus || isContractStatusCompleted,
            },
        ];
    }, [
        projectId,
        methodology,
        onCreateWorkOrder,
        orderRules.canCreate,
        isContractStatusWork,
        isDisabledByProjectStatus,
        handleUploadContract,
        contractRules?.canCreate,
        contractRules?.canEdit,
        isAvailableWorkOrder,
        handleEditContract,
        isContractStatusCompleted,
        orderCreateBtnTitle,
        createBimOrderHandler,
        isOrderCreateDisabled,
    ]);

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    if (!projectId) {
        return null;
    }

    const disabledDropdown = !contractRules?.canEdit && !contractRules?.canCreate;

    return (
        <>
            <DropdownContext
                items={itemsContract}
                {...propsDropdown}
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseMoveHandler={onMenuMouseMove}
                onMouseLeaveHandler={onMenuMouseLeave}
                isDisabled={disabledDropdown}
            />

            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="upCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
            {CreateOrderConfirmationDialog}
        </>
    );
};

KebabTreeContract.displayName = 'KebabTreeContract';
